import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  tenantId: string;
  tenantName?: string;
  companyName?: string;
  enabledModules?: string[] | null;
  availableTenants?: {
    userId: string;
    tenantId: string;
    name: string;
    slug: string;
    logo?: string;
    primaryColor?: string;
    role: string;
    isCurrent: boolean;
  }[];
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string, tenantId?: string) => Promise<void>;
  register: (data: any) => Promise<void>;
  switchTenant: (tenantId: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(sessionStorage.getItem('token') || localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const validateToken = async () => {
      if (token) {
        try {
          const response = await api.get('/auth/profile', {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data.user || response.data);
        } catch (error) {
          setUser(null);
          setToken(null);
          sessionStorage.removeItem('token');
          localStorage.removeItem('token');
        }
      }
      setLoading(false);
    };
    validateToken();
  }, []);

  const login = async (email: string, password: string, tenantId?: string) => {
    const response = await api.post('/auth/login', { 
      email: email.trim(), 
      password,
      ...(tenantId ? { tenantId } : {})
    });
    const { user, token } = response.data;
    setUser(user);
    setToken(token);
    sessionStorage.setItem('token', token);
    localStorage.setItem('token', token);
    if (user?.tenantId) {
      localStorage.setItem('nexo_active_tenant_id', user.tenantId);
    }
  };

  const register = async (data: any) => {
    const response = await api.post('/auth/register', data);
    const { user, token } = response.data;
    setUser(user);
    setToken(token);
    sessionStorage.setItem('token', token);
  };

  const switchTenant = async (tenantId: string) => {
    try {
      const response = await api.post('/auth/switch-tenant', { tenantId });
      const { user: updatedUser, token: newToken } = response.data;
      setToken(newToken);
      sessionStorage.setItem('token', newToken);
      localStorage.setItem('token', newToken);
      localStorage.setItem('nexo_active_tenant_id', tenantId);
      setUser(updatedUser);
      window.location.href = '/dashboard';
    } catch (err) {
      console.error('Error switching tenant:', err);
      throw err;
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    sessionStorage.removeItem('token');
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, switchTenant, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
